import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/shared/ui/card";
import { ClipboardList, Search } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { usePropertyAttributes } from "@/features/properties/hooks/usePropertyAttributes";
import type { AttributeField, NodeField } from "@/features/properties/types/propertyAttributes";

import { getCategoryAccentClasses, getCategoryIcon } from "./propertyOverview/categoryIcons";
import { AttributeFieldRenderer } from "./propertySiteAttributes/NodeSection";
import { PropertySiteAttributesSkeleton } from "./propertySiteAttributes/PropertySiteAttributesSkeleton";
import { fieldMatchesQuery, getCategoryAnchorId, getFieldAnchorId } from "./propertySiteAttributes/searchHelpers";

interface PropertySiteAttributesTabProps {
  propertyId: string;
  /** Rendered on the right of the header row (e.g. an Export button). */
  headerActions?: React.ReactNode;
}

export function PropertySiteAttributesTab({
  propertyId,
  headerActions,
}: PropertySiteAttributesTabProps) {
  const { data: attributes, isLoading, error } = usePropertyAttributes(propertyId);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const focusFieldId = searchParams.get("focusFieldId");

  // Deep link from the Data Completeness card: scroll to the unanswered
  // question and highlight it briefly so the user can spot it.
  useEffect(() => {
    if (!focusFieldId || isLoading || !attributes?.length) return;
    const el = document.getElementById(getFieldAnchorId({ id: focusFieldId }));
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    const highlightClasses = ["ring-2", "ring-primary", "bg-primary/10"];
    el.classList.add(...highlightClasses);
    const timer = setTimeout(() => el.classList.remove(...highlightClasses), 2500);
    return () => {
      clearTimeout(timer);
      el.classList.remove(...highlightClasses);
    };
  }, [focusFieldId, isLoading, attributes]);

  if (isLoading) {
    return <PropertySiteAttributesSkeleton />;
  }

  if (error) {
    return (
      <div className="bg-card/30 border border-dashed border-border rounded-xl p-12 text-center">
        <p className="text-sm text-destructive">
          Failed to load site attributes. Please try again later.
        </p>
      </div>
    );
  }

  if (!attributes || attributes.length === 0) {
    return (
      <div className="bg-card/30 border border-dashed border-border rounded-xl p-12 text-center">
        <div className="flex justify-center mb-4">
          <div className="p-4 rounded-full bg-muted">
            <ClipboardList className="h-8 w-8 text-muted-foreground" />
          </div>
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">
          No Site Attributes
        </h3>
        <p className="text-sm text-muted-foreground">
          No site attribute data has been recorded for this property yet.
        </p>
      </div>
    );
  }

  const sortedAttributes = [...attributes].sort((a, b) =>
    (a.title ?? "").localeCompare(b.title ?? "")
  );
  const categoryFields = sortedAttributes.filter(
    (field): field is NodeField => field.type === "node"
  );
  const uncategorizedFields = sortedAttributes.filter(
    (field: AttributeField) => field.type !== "node"
  );
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredCategories = normalizedSearch
    ? categoryFields.filter((field) => fieldMatchesQuery(field, normalizedSearch))
    : categoryFields;

  return (
    <div className="space-y-6 lg:flex lg:h-full lg:flex-col lg:space-y-0 lg:gap-6 lg:overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 lg:flex-shrink-0">
        <div>
          <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-primary" />
            Site Attributes
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Property details and site survey data
          </p>
        </div>
        {headerActions && (
          <div className="flex-shrink-0">{headerActions}</div>
        )}
      </div>

      {/* Survey categories */}
      <div className="grid grid-cols-1 lg:grid-cols-[350px_1fr] gap-6 lg:min-h-0 lg:flex-1 lg:overflow-hidden">
        <Card className="bg-card/50 border border-border/50 h-fit lg:h-full lg:max-h-full lg:overflow-hidden">
          <CardContent className="p-4 space-y-4 lg:flex lg:h-full lg:flex-col">
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                Survey categories
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                Search and jump to a category
              </p>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search categories..."
                className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            <div className="space-y-1 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:px-1 lg:py-1">
              {filteredCategories.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">
                  No categories found.
                </p>
              ) : (
                filteredCategories.map((field) => {
                  const Icon = getCategoryIcon(field.title);
                  const isSelected = selectedCategoryId === field.id;
                  return (
                    <a
                      key={field.id}
                      href={`#${getCategoryAnchorId(field)}`}
                      onClick={() => setSelectedCategoryId(field.id)}
                      className={cn(
                        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                        isSelected
                          ? "bg-primary/10 text-primary font-medium ring-1 ring-primary/30"
                          : "text-muted-foreground hover:bg-accent/40 hover:text-foreground"
                      )}
                    >
                      <span
                        className={cn(
                          "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors",
                          getCategoryAccentClasses(field.title)
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="flex-1 truncate">{field.title}</span>
                      <span
                        className={cn(
                          "shrink-0 text-xs tabular-nums",
                          isSelected ? "text-primary" : "text-muted-foreground/60"
                        )}
                      >
                        {field.fields.length}
                      </span>
                    </a>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4 lg:min-h-0 lg:overflow-y-auto lg:pr-2">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              Survey categories
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {filteredCategories.length} of {categoryFields.length} categories
            </p>
          </div>

          {filteredCategories.length === 0 ? (
            <div className="bg-card/30 border border-dashed border-border rounded-xl p-8 text-center">
              <p className="text-sm text-muted-foreground">
                No survey categories match your search.
              </p>
            </div>
          ) : (
            filteredCategories.map((field) => (
              <AttributeFieldRenderer
                key={field.id}
                field={field}
                depth={0}
                defaultOpen
              />
            ))
          )}

          {uncategorizedFields.length > 0 && (
            <div className="space-y-2 pt-2">
              <h3 className="text-sm font-semibold text-muted-foreground">
                Uncategorized fields
              </h3>
              <div className="space-y-1">
                {uncategorizedFields.map((field) => (
                  <AttributeFieldRenderer
                    key={field.id}
                    field={field}
                    depth={0}
                    defaultOpen
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
